import React from 'react';
import { Card, CardHeader, CardBody, Badge, Button } from 'reactstrap';
import './PCComplianceModule.css';

interface PCComplianceModuleProps {
  id?: number;
  isCompact?: boolean;
}

/**
 * Policy Compliance Module component
 * Displays information about the Policy Compliance module
 * Can be displayed in compact or full view
 */
const PCComplianceGuardFallback: React.FC<PCComplianceModuleProps> = ({
  id,
  isCompact = false,
}) => {
  return isCompact ? <CompactView id={id} /> : <FullView id={id} />;
};

const CompactView: React.FC<{ id?: number }> = ({ id }) => (
  <Card className="pc-module-compact">
    <CardHeader>
      <div className="d-flex align-items-center">
        <h4 className="mb-0 text-white mr-3">Policy Compliance Module</h4>
        <Badge className="premium-tag">Add-on</Badge>
      </div>
    </CardHeader>

    <CardBody>
      <p className="mb-2 mt-2">
        The Policy Compliance module (an add-on paid feature) is a highly
        effective tool for audit readiness. It allows organizations to assess
        their compliance against standards — such as <strong>SOC 2</strong>,{' '}
        <strong>ISO 27001</strong>, <strong>FedRAMP</strong>,{' '}
        <strong>CMMC</strong> and other industry standards by applying
        corresponding policy templates.
      </p>

      <ul className="list-unstyled mb-3">
        <li className="mb-2">
          <i className="fa fa-check text-success mr-2"></i>
          <span>
            Align with <strong>SOC 2</strong>, <strong>ISO 27001</strong>, and
            other standards
          </span>
        </li>
        <li className="mb-2">
          <i className="fa fa-check text-success mr-2"></i>
          <span>Assess physical, virtual, cloud, and on-prem systems</span>
        </li>
        <li className="mb-2">
          <i className="fa fa-check text-success mr-2"></i>
          <span>Get immediate pass/fail results and compliance overview</span>
        </li>
      </ul>
      <div className="d-flex justify-content-between align-items-center mt-3">
        <Button
          color="primary"
          tag="a"
          role="link"
          href="mailto:support@securitypoint360.com"
        >
          <i className="fa fa-envelope mr-2"></i> Contact Us
        </Button>
        {id && <div className="text-muted small">CA ID: {id}</div>}
      </div>
    </CardBody>
  </Card>
);

const FullView: React.FC<{ id?: number }> = ({ id }) => (
  <Card>
    <CardHeader className="bg-primary text-white">
      <div className="d-flex align-items-center">
        <h3 className="mb-0 mr-3">Policy Compliance Module</h3>
        <Badge color="light" className="text-dark">
          Add-on
        </Badge>
      </div>
    </CardHeader>

    <CardBody>
      <p className="mb-4 mt-2 w-50">
        The Policy Compliance module (an add-on paid feature) is a highly
        effective tool for audit readiness. It allows organizations to assess
        their compliance against standards—such as <strong>SOC 2</strong>,{' '}
        <strong>ISO 27001</strong>, <strong>FedRAMP</strong>,{' '}
        <strong>CMMC</strong> and other industry standards by applying
        corresponding policy templates.
      </p>

      <h4 className="mb-3">Key Benefits:</h4>

      <div className="d-flex align-items-start mb-3">
        <div className="mr-3 text-primary">
          <i className="fa fa-shield-alt fa-lg"></i>
        </div>
        <div>
          <h6>Standards Alignment</h6>
          <p className="text-muted">
            Evaluate compliance against <strong>SOC 2</strong>,{' '}
            <strong>ISO 27001</strong>, <strong>FedRAMP</strong>,{' '}
            <strong>CMMC</strong> and other industry standards by applying
            corresponding policy templates.
          </p>
        </div>
      </div>

      <div className="d-flex align-items-start mb-3">
        <div className="mr-3 text-primary">
          <i className="fa fa-server fa-lg"></i>
        </div>
        <div>
          <h6>Multi-Environment Assessment</h6>
          <p className="text-muted">
            The module evaluates all cloud agents (including physical and
            virtual servers and workstations across any environment) for
            comprehensive coverage.
          </p>
        </div>
      </div>

      <div className="d-flex align-items-start mb-3">
        <div className="mr-3 text-primary">
          <i className="fa fa-tachometer-alt fa-lg"></i>
        </div>
        <div>
          <h6>Real-time Compliance Insights</h6>
          <p className="text-muted">
            Identifies whether specific audit controls are passing or failing,
            providing a clear view of both individual control status and overall
            organizational compliance.
          </p>
        </div>
      </div>

      <hr className="my-4" />

      <div className="d-flex justify-content-between align-items-center">
        <Button
          color="primary"
          role="link"
          tag="a"
          href="mailto:support@securitypoint360.com"
        >
          <i className="fa fa-envelope mr-2"></i> Contact Us
        </Button>
        <div className="text-muted small">{id && <span>CA ID: {id}</span>}</div>
      </div>
    </CardBody>
  </Card>
);

export default PCComplianceGuardFallback;
